import React, { useId } from 'react';
import { cn } from '@/utils/cn';
import { FieldShell, describedBy } from '@/components/UI/Field';
import { fieldClass } from '@/components/UI/fieldStyles';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Classes for the outer wrapper. `className` goes to the <textarea> itself. */
  wrapperClassName?: string;
}

/** Multi-line field: same recipe as Input but a 20px radius, since a full pill would look wrong once it grows. */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, wrapperClassName, label, error, hint, id, required, rows = 4, ...props }, ref) => {
    const autoId = useId();
    const textareaId = id ?? autoId;

    return (
      <FieldShell id={textareaId} label={label} hint={hint} error={error} required={required} className={wrapperClassName}>
        <textarea
          id={textareaId}
          ref={ref}
          rows={rows}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(textareaId, hint, error)}
          className={cn(fieldClass({ error: Boolean(error), shape: 'box' }), 'min-h-[5.5rem] resize-y', className)}
          {...props}
        />
      </FieldShell>
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
